import { useCallback, useEffect, useRef, useState } from "react";
import { clearSession, isAuthenticated, logout } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  LAST_ACTIVITY_KEY,
  noteIdleSignOut,
  readLastActivity,
  recordActivity,
} from "@/lib/idle-session";

const MINUTE_MS = 60_000;
/** The warning comes this long before the sign-out (halfway, for a timeout under 4 minutes). */
const WARN_BEFORE_MS = 2 * MINUTE_MS;
/** Activity is written to storage at most this often. */
const RECORD_EVERY_MS = 10_000;
const TICK_MS = 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll", "mousemove"];

function clock(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

const minutesWord = (n: number) => `${n} ${n === 1 ? "minute" : "minutes"}`;

/**
 * Signs the director out after `minutes` without activity in any tab of this browser, warning
 * two minutes before; 0 turns it off. Closing the warning (the button or Esc) counts as
 * activity; moving the mouse while it is open does not. Mounted once in the signed-in shell.
 *
 * The idle time is read from localStorage on every tick, not counted here, so a laptop that
 * slept or a tab whose timers the browser slowed still signs out on time, and a page opened
 * after the timeout has passed signs out at once.
 */
export function IdleSignOut({ minutes }: { minutes: number }) {
  /** Time left before the sign-out while the warning shows, else null. */
  const [left, setLeft] = useState<number | null>(null);
  const warningOpen = useRef(false);
  /** Closing the warning: the effect's `reset`, which knows the timeout. */
  const resetRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!minutes) return;
    const timeoutMs = minutes * MINUTE_MS;
    const warnAtMs = timeoutMs - Math.min(WARN_BEFORE_MS, timeoutMs / 2);
    let lastRecorded = 0;
    let signingOut = false;

    const check = () => {
      if (signingOut) return;
      const idle = Date.now() - readLastActivity();
      if (idle >= timeoutMs) {
        signingOut = true;
        warningOpen.current = false;
        setLeft(null);
        // Back from the back/forward cache, this tab can tick before it hears that another
        // tab already ended the session (Sign out there): nothing is left to time out.
        if (!isAuthenticated()) return clearSession();
        noteIdleSignOut(minutes);
        void logout();
        return;
      }
      warningOpen.current = idle >= warnAtMs;
      setLeft(warningOpen.current ? timeoutMs - idle : null);
    };
    /** Restart the clock (other tabs see it too), unless the timeout has already passed
     * unnoticed (a laptop that slept, a throttled tab): the first move on waking is no
     * reprieve, so that signs out instead. */
    const reset = () => {
      const now = Date.now();
      if (now - readLastActivity() >= timeoutMs) return check();
      lastRecorded = now;
      recordActivity(now);
      check();
    };
    resetRef.current = reset;
    const onActivity = () => {
      if (warningOpen.current) return; // only closing the warning resets the clock
      if (Date.now() - lastRecorded >= RECORD_EVERY_MS) reset();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && warningOpen.current) reset();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_KEY) check();
    };

    check();
    const tick = window.setInterval(check, TICK_MS);
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, onActivity, { capture: true, passive: true });
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("storage", onStorage);
    return () => {
      window.clearInterval(tick);
      for (const type of ACTIVITY_EVENTS) {
        window.removeEventListener(type, onActivity, { capture: true });
      }
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("storage", onStorage);
    };
  }, [minutes]);

  const stay = useCallback(() => resetRef.current(), []);

  if (left === null || !minutes) return null;
  const idleMinutes = Math.floor((minutes * MINUTE_MS - left) / MINUTE_MS);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071a3f]/70 p-4 backdrop-blur-[2px]">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-title"
        aria-describedby="idle-text"
        className="w-full max-w-md rounded-xl border bg-card p-6 shadow-md"
      >
        <h2 id="idle-title" className="text-lg font-semibold">
          Are you still there?
        </h2>
        <p id="idle-text" className="mt-2 text-sm text-muted-foreground">
          You will be signed out in {clock(left)} because there has been no activity for{" "}
          {minutesWord(idleMinutes)}.
        </p>
        <div className="mt-5 flex justify-end">
          <Button autoFocus onClick={stay}>
            Stay signed in
          </Button>
        </div>
      </div>
    </div>
  );
}
